/*
  Glimot favourites (guest, this device only).

  Storage: localStorage "glimot.favourites" = { version: 2, items: [...] }, each item:
    { productId, handle, savedAt, source: 'heart' | 'cart',
      variantId?, options?: [{ name, value }], reenter?: boolean }
  - Never stored: personalisation text, gift messages, notes, customer details or prices.
    Prices and availability are always loaded live (Glimot.productData).
  - Expiry: each item is removed `expiryDays` after its own savedAt (Theme settings → Glimot).
    Expired items are deleted and cannot be restored if the setting is later increased.
  - Account sync: NOT implemented. `GlimotFavourites.onSignInMerge` is a reserved hook for a future
    secure backend; it is never called by this theme and nothing claims syncing works.
*/
(() => {
  // Check for the API itself: an element with a matching id would also appear on window.
  if (window.GlimotFavourites && typeof window.GlimotFavourites.renderPreview === 'function') return;

  const STORAGE_KEY = 'glimot.favourites';
  const VERSION = 2;
  const cfg = (window.glimot && window.glimot.wishlist) || {};
  const strings = (window.glimot && window.glimot.strings) || {};
  const DAY = 86400000;

  function expiryDays() {
    const days = Math.floor(Number(cfg.expiryDays));
    return Number.isFinite(days) && days >= 1 ? days : 7;
  }

  function safeRead() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return [];
      return parsed.items.filter((item) => item && item.productId && item.handle && item.savedAt);
    } catch (e) {
      return [];
    }
  }

  function safeWrite(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: VERSION, items }));
    } catch (e) {
      /* storage unavailable (private mode etc.): favourites simply don't persist */
    }
  }

  // Only these fields are ever written; anything else (e.g. text) is dropped.
  function sanitise(item) {
    const clean = {
      productId: Number(item.productId),
      handle: String(item.handle),
      savedAt: Number(item.savedAt) || Date.now(),
      source: item.source === 'cart' ? 'cart' : 'heart',
    };
    if (item.variantId) clean.variantId = Number(item.variantId);
    if (Array.isArray(item.options)) {
      clean.options = item.options
        .filter((option) => option && option.name && option.value)
        .map((option) => ({ name: String(option.name), value: String(option.value) }));
    }
    if (item.reenter) clean.reenter = true;
    return clean;
  }

  function prune(items) {
    const cutoff = Date.now() - expiryDays() * DAY;
    return items.filter((item) => item.savedAt >= cutoff);
  }

  function load() {
    const items = safeRead();
    const kept = prune(items);
    if (kept.length !== items.length) safeWrite(kept);
    return kept;
  }

  function save(items) {
    safeWrite(items.map(sanitise));
    emit();
  }

  function emit() {
    document.dispatchEvent(new CustomEvent('glimot:favourites-change', { detail: { items: load() } }));
  }

  const api = {
    list: () => load().sort((a, b) => b.savedAt - a.savedAt),
    count: () => load().length,
    has: (productId) => load().some((item) => item.productId === Number(productId)),
    addHeart(productId, handle) {
      const items = load();
      if (items.some((item) => item.productId === Number(productId) && item.source === 'heart')) return;
      items.push({ productId, handle, savedAt: Date.now(), source: 'heart' });
      save(items);
    },
    removeProduct(productId) {
      save(load().filter((item) => item.productId !== Number(productId)));
    },
    removeEntry(productId, variantId) {
      save(
        load().filter(
          (item) => !(item.productId === Number(productId) && (item.variantId || null) === (variantId ? Number(variantId) : null))
        )
      );
    },
    saveFromCart({ productId, handle, variantId, options, reenter }) {
      const items = load().filter(
        (item) => !(item.productId === Number(productId) && item.variantId === Number(variantId))
      );
      items.push({ productId, handle, variantId, options, reenter: !!reenter, savedAt: Date.now(), source: 'cart' });
      save(items);
    },
    // Reserved for a future, secure sign-in merge. Not implemented; never called by the theme.
    onSignInMerge: null,
  };

  /* ---------- Helpers ---------- */
  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([key, value]) => {
      if (value === null || value === undefined || value === false) return;
      if (key === 'class') node.className = value;
      else if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value === true ? '' : value);
    });
    (Array.isArray(children) ? children : [children]).forEach((child) => child && node.append(child));
    return node;
  }

  function t(key, fallback) {
    return (api.strings && api.strings[key]) || strings[key] || fallback || '';
  }

  function optionsText(item) {
    return (item.options || []).map((option) => `${option.name}: ${option.value}`).join(' / ');
  }

  function iconSvg(name) {
    const template = document.querySelector(`template[data-g-icon="${name}"]`);
    return template ? template.content.cloneNode(true) : document.createTextNode('');
  }

  /* ---------- Hearts ---------- */
  class GlimotFavouriteButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.productId = Number(this.dataset.productId);
      this.handle = this.dataset.handle;
      this.title = this.dataset.title || '';
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      });
      this.onChange = () => this.render();
      document.addEventListener('glimot:favourites-change', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('glimot:favourites-change', this.onChange);
    }

    toggle() {
      if (api.has(this.productId)) {
        api.removeProduct(this.productId);
        window.Glimot?.announce(t('favouriteRemoved', 'Removed from favourites'));
      } else {
        api.addHeart(this.productId, this.handle);
        window.Glimot?.announce(t('favouriteAdded', 'Added to favourites'));
        showToast(document.querySelector('a[href*="/account/logout"]') ? 'Added to your wishlist' : 'Added to your favourites', false, 3000);
      }
    }

    render() {
      const pressed = api.has(this.productId);
      this.button.setAttribute('aria-pressed', String(pressed));
      this.classList.toggle('is-active', pressed);
      const label = pressed ? t('removeFavourite', 'Remove from favourites') : t('addFavourite', 'Add to favourites');
      this.button.setAttribute('aria-label', this.title ? `${label}: ${this.title}` : label);
    }
  }

  if (!customElements.get('glimot-favourite-button')) {
    customElements.define('glimot-favourite-button', GlimotFavouriteButton);
  }

  /* ---------- Header count ---------- */
  function updateCounts() {
    const count = api.count();
    document.querySelectorAll('[data-g-favourites-count]').forEach((node) => {
      node.textContent = count > 99 ? '99+' : String(count);
      node.hidden = count === 0;
    });
    document.querySelectorAll('[data-g-favourites-count-text]').forEach((node) => {
      node.textContent = count ? ` (${count})` : '';
    });
    document.querySelectorAll('[data-g-view-all-favourites]').forEach((node) => {
      const template = node.dataset.template || node.textContent;
      node.dataset.template = template;
      node.textContent = template.includes('[count]') ? template.replace('[count]', count) : template;
    });
  }

  /* ---------- Product rows (drawer and preview) ---------- */
  function renderRowBase(item, data, { compact }) {
    const url = data ? data.url : window.Glimot.productUrl(item.handle);
    const variant = data && item.variantId ? data.variants.find((v) => v.id === item.variantId) : null;
    const image = (variant && variant.image) || (data && data.image);
    const row = el('li', { class: compact ? 'g-fav-preview__item' : 'g-fav-item' });
    const media = el('a', { href: url, class: compact ? 'g-fav-preview__media' : 'g-fav-item__media', tabindex: '-1', 'aria-hidden': 'true' });
    if (image) media.append(el('img', { src: image, alt: '', loading: 'lazy', width: '120', height: '120' }));
    row.append(media);
    const info = el('div', { class: compact ? 'g-fav-preview__info' : 'g-fav-item__info' });
    info.append(el('a', { href: url, class: compact ? 'g-fav-preview__title' : 'g-fav-item__title', text: data ? data.title : item.handle }));
    if (item.options && item.options.length) info.append(el('span', { class: 'g-fav-item__meta', text: optionsText(item) }));
    if (data) {
      const price = variant ? variant.price : data.priceVaries ? window.Glimot.formatTemplate(strings.fromPrice, { price: data.price }) : data.price;
      info.append(el('span', { class: compact ? 'g-fav-preview__price' : 'g-fav-item__price', text: price }));
    } else {
      info.append(el('span', { class: 'g-fav-item__meta', text: t('noLongerAvailable', 'This product is no longer available.') }));
    }
    row.append(info);
    return { row, info, variant };
  }

  function removeButton(item, compact) {
    const button = el('button', {
      type: 'button',
      class: compact ? 'g-fav-preview__remove' : 'g-fav-item__remove',
      'aria-label': `${t('removeFavourite', 'Remove from favourites')}`,
    });
    button.append(iconSvg('close'));
    button.addEventListener('click', () => {
      if (item.source === 'cart') api.removeEntry(item.productId, item.variantId);
      else api.removeProduct(item.productId);
      window.Glimot?.announce(t('favouriteRemoved', 'Removed from favourites'));
    });
    return button;
  }

  function actionArea(item, data, variant) {
    const wrap = el('div', { class: 'g-fav-item__actions' });
    const message = el('p', { class: 'g-message', role: 'status' });
    if (!data) {
      wrap.append(message);
      return wrap;
    }
    const productPage = new URL(data.url, window.location.origin);
    // Products that must be bought on their product page or a vendor page (vendor apps, required
    // personalisation, merchant rules) are never added from here — the shopper goes to that flow.
    const pageOnly = data.pageOnly === 'true' || data.pageOnly === true;
    const needsPersonalisation = item.reenter || data.personalisation === 'required' || pageOnly;

    // 1) Item saved from the cart with an exact variant.
    if (item.variantId && variant && variant.available && !needsPersonalisation) {
      const button = el('button', { type: 'button', class: 'button button--full-width', text: t('moveToCart', 'Move to cart') });
      button.addEventListener('click', () => addAndReport(button, message, { id: variant.id }, () => api.removeEntry(item.productId, item.variantId)));
      wrap.append(button, message);
      return wrap;
    }

    if (needsPersonalisation) {
      let href;
      if (data.flow === 'vendor_page' && data.destination && data.destination !== data.url) {
        // Vendor-hosted page: used exactly as the merchant entered it — no guessed parameters.
        href = data.destination;
      } else {
        if (item.variantId && variant) productPage.searchParams.set('variant', variant.id);
        if (item.reenter) productPage.searchParams.set('g_reenter', '1');
        href = productPage.pathname + productPage.search;
      }
      if (item.variantId && variant && !variant.available) {
        wrap.append(el('p', { class: 'g-message g-message--error', text: `${optionsText(item) || variant.title} — ${strings.soldOut || 'Sold out'}` }));
      }
      const label = pageOnly && !item.reenter && data.personalisation !== 'required' ? data.personaliseLabel || t('personaliseAdd', 'Personalise & add') : t('personaliseAdd', 'Personalise & add');
      wrap.append(el('a', { href, class: 'button button--full-width', text: label }));
      if (item.reenter) wrap.append(el('p', { class: 'g-fav-item__note', text: t('reenterNote', 'Please enter your personalisation again.') }));
      return wrap;
    }

    if (item.variantId && (!variant || !variant.available)) {
      const text = variant
        ? `${optionsText(item) || variant.title} — ${strings.soldOut || 'Sold out'}`
        : t('optionGone', 'This option is no longer available.');
      wrap.append(el('p', { class: 'g-message g-message--error', text }));
    }

    // 2) Only one option can be bought (a single-variant product, or every other option is sold out): add that one
    //    directly. When other options exist, the one being added is named so the shopper knows what they get.
    const buyable = (data.variants || []).filter((v) => v.available);
    if (data.hasOnlyDefaultVariant || buyable.length <= 1) {
      const only = data.hasOnlyDefaultVariant ? data.variants[0] : buyable[0];
      if (!only || !only.available) {
        wrap.append(el('p', { class: 'g-message', text: strings.soldOut || 'Sold out' }));
        return wrap;
      }
      if (!data.hasOnlyDefaultVariant) {
        wrap.append(el('p', { class: 'g-fav-item__note', text: `${only.title} — ${only.price}` }));
      }
      const button = el('button', { type: 'button', class: 'button button--full-width', text: strings.addToCart || 'Add to cart' });
      button.addEventListener('click', () =>
        addAndReport(button, message, { id: only.id }, item.source === 'cart' ? () => api.removeEntry(item.productId, item.variantId) : null)
      );
      wrap.append(button, message);
      return wrap;
    }

    // 3) Options needed: always a fresh choice (nothing pre-selected).
    const selectId = `GFav-${item.productId}-${item.variantId || 'h'}`;
    const label = el('label', { for: selectId, class: 'g-fav-item__label', text: t('chooseAnother', item.variantId ? 'Choose another option' : 'Choose an option') });
    const select = el('select', { id: selectId, class: 'g-fav-item__select select__select' });
    select.append(el('option', { value: '', text: strings.chooseOptions || 'Choose options', selected: true, disabled: true }));
    data.variants.forEach((v) => {
      select.append(el('option', { value: v.id, disabled: !v.available, text: v.available ? `${v.title} — ${v.price}` : `${v.title} — ${strings.soldOut || 'Sold out'}` }));
    });
    const button = el('button', { type: 'button', class: 'button button--full-width', text: strings.addToCart || 'Add to cart', disabled: true, 'aria-disabled': 'true' });
    select.addEventListener('change', () => {
      const ok = !!select.value;
      button.disabled = !ok;
      button.setAttribute('aria-disabled', String(!ok));
    });
    button.addEventListener('click', () => {
      if (!select.value) return;
      addAndReport(button, message, { id: Number(select.value) }, item.source === 'cart' ? () => api.removeEntry(item.productId, item.variantId) : null);
    });
    wrap.append(label, select, button, message);
    return wrap;
  }

  function addAndReport(button, message, line, onSuccess) {
    const original = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = strings.adding || 'Adding…';
    message.className = 'g-message';
    message.textContent = '';
    return window.Glimot.addToCart(line, { openCart: false }).then((result) => {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      button.textContent = original;
      if (result.ok) {
        message.className = 'g-message g-message--success';
        message.textContent = strings.added || 'Added to cart';
        window.Glimot.announce(message.textContent);
        if (onSuccess) onSuccess();
      } else {
        message.className = 'g-message g-message--error';
        message.textContent = result.message || strings.error;
      }
      return result;
    });
  }

  /* ---------- Preview in the header ---------- */
  api.renderPreview = function renderPreview(container, limit = 4) {
    if (!container) return;
    const items = api.list();
    container.replaceChildren();
    updateCounts();
    if (!items.length) {
      container.append(el('p', { class: 'g-hp__empty', text: t('empty', 'No favourites yet.') }));
      return;
    }
    const list = el('ul', { class: 'g-fav-preview__list', role: 'list' });
    container.append(list);
    items.slice(0, limit).forEach((item) => {
      const placeholder = el('li', { class: 'g-fav-preview__item g-fav-item--loading', 'aria-busy': 'true' });
      list.append(placeholder);
      window.Glimot.productData(item.handle).then((data) => {
        if (data && (data.addon === 'true' || data.addon === true)) {
          api.removeEntry(item.productId, item.variantId);
          placeholder.remove();
          return;
        }
        const { row } = renderRowBase(item, data, { compact: true });
        row.append(removeButton(item, true));
        placeholder.replaceWith(row);
      });
    });
  };

  /* ---------- Drawer ---------- */
  class GlimotFavouritesDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[role="dialog"]');
      this.list = this.querySelector('[data-g-fav-list]');
      try {
        api.strings = JSON.parse(this.querySelector('[data-g-strings]')?.textContent || '{}');
      } catch (e) {
        api.strings = {};
      }
      this.querySelectorAll('[data-g-close]').forEach((node) => node.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.addEventListener('glimot:open-favourites', (event) => this.open(event.detail && event.detail.opener));
      document.addEventListener('glimot:favourites-change', () => {
        updateCounts();
        if (this.hasAttribute('open')) this.render();
      });
    }

    open(opener) {
      window.GlimotHeader?.closeAllPanels();
      document.querySelector('cart-drawer.active')?.close();
      this.opener = opener || document.activeElement;
      this.hidden = false;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      this.render();
      requestAnimationFrame(() => {
        this.classList.add('is-visible');
        if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel.querySelector('[data-g-close]'));
      });
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.classList.remove('is-visible');
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
      window.setTimeout(() => {
        if (!this.hasAttribute('open')) this.hidden = true;
      }, 250);
    }

    render() {
      const items = api.list();
      this.list.replaceChildren();
      if (!items.length) {
        this.list.append(el('p', { class: 'g-fav-drawer__empty', text: t('empty', "You haven't saved any favourites yet.") }));
        return;
      }
      const ul = el('ul', { class: 'g-fav-drawer__items', role: 'list' });
      this.list.append(ul);
      items.forEach((item) => {
        const placeholder = el('li', { class: 'g-fav-item g-fav-item--loading', 'aria-busy': 'true' });
        ul.append(placeholder);
        window.Glimot.productData(item.handle).then((data) => {
          if (data && (data.addon === 'true' || data.addon === true)) {
            api.removeEntry(item.productId, item.variantId);
            placeholder.remove();
            return;
          }
          const { row, info, variant } = renderRowBase(item, data, { compact: false });
          info.append(actionArea(item, data, variant));
          row.append(removeButton(item, false));
          placeholder.replaceWith(row);
        });
      });
    }
  }

  if (!customElements.get('glimot-favourites-drawer')) {
    customElements.define('glimot-favourites-drawer', GlimotFavouritesDrawer);
  }

  /* ---------- Save for later (cart page and side drawer) ---------- */
  let undoBuffer = null; // held in page memory only; cleared on close or page exit
  let toast = null;

  function clearUndo() {
    undoBuffer = null;
    if (toast) {
      toast.remove();
      toast = null;
    }
  }

  window.addEventListener('pagehide', clearUndo);

  function showToast(text, withUndo, duration) {
    clearUndoToastOnly();
    toast = el('div', { class: 'g-toast', role: 'status' });
    toast.append(el('p', { class: 'g-toast__text', text }));
    if (withUndo) {
      const undo = el('button', { type: 'button', class: 'g-toast__undo', text: t('undo', 'Undo') });
      undo.addEventListener('click', runUndo);
      toast.append(undo);
    }
    const close = el('button', { type: 'button', class: 'g-toast__close', 'aria-label': t('close', 'Close') });
    close.append(iconSvg('close'));
    close.addEventListener('click', clearUndo);
    toast.append(close);
    document.body.append(toast);
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(clearUndo, duration || 12000);
  }

  function clearUndoToastOnly() {
    if (toast) {
      toast.remove();
      toast = null;
    }
  }

  function runUndo() {
    if (!undoBuffer) return;
    const { line, productId, variantId } = undoBuffer;
    const buffer = undoBuffer;
    undoBuffer = null;
    window.Glimot.addToCart(line, { openCart: false }).then((result) => {
      if (result.ok) {
        api.removeEntry(productId, variantId);
        showToast(t('undone', 'Item returned to your cart.'), false);
      } else {
        undoBuffer = buffer;
        showToast(result.message || strings.error, true);
      }
      refreshCartAreas();
    });
  }

  function refreshCartAreas() {
    document.querySelector('cart-drawer-items')?.onCartUpdate?.();
    const pageItems = document.querySelector('cart-items');
    if (pageItems && pageItems.tagName === 'CART-ITEMS' && document.getElementById('main-cart-footer')) {
      // Re-render both items and footer (Craft's own onCartUpdate only refreshes the items).
      fetch(`${window.location.pathname}?sections=${pageItems.getSectionsToRender().map((s) => s.section).join(',')}`)
        .then((response) => response.json())
        .then((sections) => {
          pageItems.getSectionsToRender().forEach((section) => {
            const host = document.getElementById(section.id);
            if (!host || !sections[section.section]) return;
            const target = host.querySelector(section.selector) || host;
            const source = new DOMParser().parseFromString(sections[section.section], 'text/html').querySelector(section.selector);
            if (source) target.innerHTML = source.innerHTML;
          });
        })
        .catch(() => {});
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-g-save-for-later]');
    if (!button) return;
    event.preventDefault();
    // Vendor data ("_" properties) belongs to the vendor's own flow: the item is saved with the
    // "re-enter" flag and can only come back through the product/customizer page (no Undo).
    const hasVendorData = button.dataset.hasVendorData === 'true';
    const hasPersonalisation = button.dataset.hasPersonalisation === 'true' || hasVendorData;
    if (hasPersonalisation && button.dataset.confirmed !== 'true') {
      // Explain first: the text is not saved and must be re-entered later.
      button.dataset.confirmed = 'true';
      const note = button.parentElement.querySelector('[data-g-save-note]');
      if (note) note.hidden = false;
      button.textContent = t('saveAnyway', 'Save for later anyway');
      window.Glimot.announce(note ? note.textContent : '');
      return;
    }

    let options = [];
    try {
      options = JSON.parse(button.dataset.options || '[]');
    } catch (e) {
      options = [];
    }
    const productId = Number(button.dataset.productId);
    const variantId = Number(button.dataset.variantId);
    api.saveFromCart({ productId, handle: button.dataset.handle, variantId, options, reenter: hasPersonalisation });

    // Undo copy: in page memory only (may include the line's personalisation, read from the cart).
    let properties = null;
    try {
      properties = JSON.parse(button.dataset.properties || 'null');
    } catch (e) {
      properties = null;
    }
    undoBuffer = hasVendorData ? null : { productId, variantId, line: { id: variantId, quantity: Number(button.dataset.quantity) || 1, properties } };
    // The DOM copy of the properties is removed with the line when the cart re-renders.
    button.removeAttribute('data-properties');

    const cartItems = button.closest('cart-items, cart-drawer-items');
    const index = button.dataset.index;
    if (cartItems && typeof cartItems.updateQuantity === 'function' && index) {
      cartItems.updateQuantity(index, 0, event);
    } else if (button.dataset.lineKey) {
      window.Glimot.changeLine(button.dataset.lineKey, 0).then(refreshCartAreas);
    }
    showToast(
      hasPersonalisation ? `${t('savedForLater', 'Saved to favourites.')} ${t('personalisationNote', '')}` : t('savedForLater', 'Saved to favourites.'),
      !!undoBuffer
    );
  });

  window.GlimotFavourites = api;
  updateCounts();
  emit();
})();
